import { isMockedData, mockedTenantId } from "@constants/demo";
import { ENDPOINT_ASSESSOR } from "@constants/urls";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useAuthContext } from "@memorang/applets";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui";
import { useLocalSearchParams } from "expo-router";
import { runOnJS } from "react-native-reanimated";
import EventSource from "react-native-sse";
import { useEMRStore } from "../components/emr-card/useEMRStore";
import { MAPPED_ITEM_TYPES } from "../constants";
import { uploadImage, uploadRecording } from "../helpers/recording";
import { transcribeAudio } from "../helpers/transcription";
import useAIFeedbackStore, {
	type TimestampedEmotions,
} from "./useAIFeedbackStore";
import useSessionStore from "./useSessionStore";

type GenerateExplanationParams = {
	currentItem: BaseItemSchema;
	itemType: OriginalItemTypes;
	renderableItemType: RenderableItemTypes;
	onAiFeedbackProvided: () => void;
	audioUrl?: string | null;
	existingUserResponse?: string;
	markedImageUrl?: string | null;
	audioTranscription?: string;
};

const defaultTaskTypes: Partial<Record<RenderableItemTypes, string>> = {
	AUDIO_INPUT: "audioAnswer",
	WRITING_INPUT: "textAnswer",
	MARKING_ANSWER: "markingAnswer",
	WORKFLOW_GRAPH: "general",
	AUDIO_INPUT_EMOTION: "audioEmotion",
};

const MAX_RETRIES = 3;

const formatLanguageCode = (languageCode: string) => {
	return languageCode.replace(/-/g, "_").toLowerCase();
};

export const replaceBrackets = (str: string): string =>
	str.replace(/[{}[\]]/g, "");

const updateTopFiveEmotions = (
	emotionPredictions: TimestampedEmotions,
	currentItem: BaseItemSchema,
) => {
	const emotionScores: Record<string, { totalScore: number; count: number }> =
		{};
	// Aggregate scores for each emotion
	for (const emotions of Object.values(emotionPredictions)) {
		for (const { name, score } of emotions) {
			if (!emotionScores[name]) {
				emotionScores[name] = { totalScore: 0, count: 0 };
			}
			emotionScores[name].totalScore += score;
			emotionScores[name].count += 1;
		}
	}
	// Calculate average score for each emotion
	const averageScores = Object.entries(emotionScores).map(
		([name, { totalScore, count }]) => ({
			name,
			score: totalScore / count, // Renamed averageScore to score
		}),
	);
	// Sort emotions by average score and select top five
	const topFive = averageScores.sort((a, b) => b.score - a.score).slice(0, 5);
	useAIFeedbackStore
		.getState()
		.updateTopFiveEmotions(currentItem.id!.toString(), topFive);
};

/**
 * Formats the item for the AI feedback endpoint based on the item type
 * @param item
 */
export const formatItemForAI = (item: BaseItemSchema) => {
	const { stimulus, content } = item;

	if (!stimulus?.metadata.type) {
		return null;
	}

	const baseFormat = {
		instruction: stimulus.content.instruction,
		stem: content?.stem,
	};

	switch (stimulus.metadata.type) {
		case "RLS_STIMULUS": {
			const readingPassage =
				stimulus.content.slides?.find((slide) => slide.title === "Read")
					?.passage || stimulus.content.passage;

			const listeningTranscript =
				stimulus.content.slides
					?.find((slide) => slide.title === "Listen")
					?.media?.map((media) => media.transcript) ||
				stimulus.content.media?.map((media) => media.transcript);

			return {
				...baseFormat,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "LS_STIMULUS": {
			const listeningTranscript =
				stimulus.content.slides
					?.flatMap((slide) => slide.media?.map((media) => media.transcript))
					.filter(Boolean) ||
				stimulus.content.media?.map((media) => media.transcript);

			return {
				...baseFormat,
				passageTitle: stimulus.content.passage?.title,
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "ACADEMIC_DISCUSSION_STIMULUS": {
			return {
				...baseFormat,
				className: stimulus.content.className,
				professorName: stimulus.content.professor?.name,
				questionTitle: stimulus.content.professor?.questionTitle,
				questionBody: stimulus.content.professor?.questionBody,
				discussion: content?.discussion
					?.map((d) => `${d.student.name}: ${d.post}`)
					.join("\n\n"),
			};
		}

		case "INTEGRATED_WRITING_STIMULUS": {
			const readingPassage =
				stimulus.content.slides?.find(
					(slide) => slide.title === "Reading Passage",
				)?.passage || stimulus.content.passage;

			const listeningTranscript =
				stimulus.content.slides
					?.find((slide) => slide.title === "Listening Scene")
					?.media?.map((media) => media.transcript) ||
				stimulus.content.media?.map((media) => media.transcript);

			return {
				...baseFormat,
				passageTitle: stimulus.content.passage?.title,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "INDEPENDENT_SPEAKING_STIMULUS":
		case "INTEGRATED_MARKING_ANSWER_STIMULUS": {
			const transcripts =
				stimulus.content.slides
					?.flatMap((slide) => slide.media?.map((media) => media.transcript))
					.filter(Boolean) ||
				stimulus.content.media?.map((media) => media.transcript);

			return {
				...baseFormat,
				transcripts: transcripts?.join("\n"),
			};
		}

		case "READING": {
			return {
				...baseFormat,
				passage: JSON.stringify(stimulus.content.passage),
				stem: item.content?.stem,
				passage_video_transcript:
					stimulus.content.media?.map((media) => media.transcript) ?? "",
			};
		}

		default:
			return baseFormat;
	}
};

export const getParentStem = (currentItem: BaseItemSchema) => {
	const { stimulus, content, metadata } = currentItem;

	let parentStem = "";

	if (stimulus?.content) {
		parentStem = JSON.stringify(stimulus?.content);
	}

	if (content?.discussion) {
		parentStem += `\n\nDiscussion: ${JSON.stringify(content?.discussion)}`;
	}

	if (content?.instruction) {
		parentStem += `\n\nInstruction: ${content?.instruction}`;
	}

	if (metadata?.skill) {
		parentStem += `\n\nCurrent Skill: ${metadata?.skill}`;
	}

	return replaceBrackets(parentStem);
};

export const useAIFeedback = () => {
	const sessionItems = useSessionStore((state) => state.sessionItems);

	const submitUserData = useEMRStore((state) => state.submitUserData);
	const sessionId = useSessionStore((state) => state.id);
	const setStreaming = useAIFeedbackStore((store) => store.setStreaming);
	const setShowError = useAIFeedbackStore((store) => store.setShowError);
	const userResponse = useAIFeedbackStore((store) => store.userResponse);
	const audioResponse = useAIFeedbackStore((store) => store.audioResponse);
	const updateRunDetails = useAIFeedbackStore(
		(store) => store.updateRunDetails,
	);
	const updateSubskillAssessments = useAIFeedbackStore(
		(store) => store.updateSubskillAssessments,
	);
	const updateScore = useAIFeedbackStore((store) => store.updateScore);
	const updateExplanation = useAIFeedbackStore(
		(store) => store.updateExplanation,
	);
	const updateAnswer = useAIFeedbackStore((store) => store.updateAnswer);
	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;
	const tenantId = useAppStore((store) => store.tenant.id);
	const { showToast } = useToast();
	const { viewerId: userId } = useAuthContext();
	const language = formatLanguageCode(i18n.language);
	const { currentExam } = useDemoExamStore();

	const handleGenerateExplanation = async ({
		currentItem,
		itemType,
		renderableItemType,
		audioUrl,
		existingUserResponse,
		onAiFeedbackProvided,
		markedImageUrl,
		audioTranscription,
	}: GenerateExplanationParams) => {
		const { stimulus } = currentItem;

		const assessmentType =
			itemType === "AUDIO_INPUT" || itemType === "AUDIO_INPUT_EMOTION"
				? "langgraph-speaking"
				: itemType === "INTERACTIVE_VIDEO"
					? "langgraph-interactive-video"
					: "langgraph-writing";

		let stimulusTaskType: string = stimulus?.metadata?.type! ?? "";

		const aiResponse = useAIFeedbackStore.getState().aiResponse;
		const emotionPredictions =
			aiResponse?.[currentItem.id!]?.emotionPredictions;

		if (
			stimulusTaskType &&
			["LS_STIMULUS", "RLS_STIMULUS"].includes(stimulusTaskType)
		) {
			stimulusTaskType = "INTEGRATED_SPEAKING_STIMULUS";
		}

		const formattedItem = formatItemForAI(currentItem);
		const answer =
			itemType === "AUDIO_INPUT" || itemType === "AUDIO_INPUT_EMOTION"
				? audioUrl
				: existingUserResponse;
		const isMarkingAnswer = itemType === "MARKING_ANSWER";
		if (answer) {
			updateAnswer(currentItem.id!, answer);
		}

		const taskType =
			currentItem.metadata?.taskType ??
			defaultTaskTypes[renderableItemType] ??
			"default";

		const imageUrls = [
			...(currentItem.content?.media
				?.map((media) => {
					const url = media.url;
					const isValidUrl =
						typeof url === "string" &&
						url.length > 0 &&
						/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url.toLowerCase());
					return isValidUrl ? url : null;
				})
				.filter(Boolean) ?? []),
			...(isMockedData
				? (currentItem.stimulus?.content?.slides
						?.flatMap((slide) =>
							slide.media?.map((media) => {
								const url = media.url;
								const isValidImageUrl =
									url &&
									/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url.toLowerCase());
								return isValidImageUrl ? url : null;
							}),
						)
						?.filter(Boolean) ?? [])
				: []),
		].filter(Boolean);

		const body = {
			tenantId: isMockedData ? mockedTenantId : tenantId,
			...(isMockedData ? { agentId: currentExam.agentId } : {}),
			userId,
			options: {
				subtaskType:
					assessmentType === "langgraph-interactive-video"
						? "langgraph-speaking"
						: assessmentType,
				input: {
					...formattedItem,
					...(isMockedData ? { task_type: taskType } : {}),
					stimulusTaskType,
					assessment_mode: "Formative",
					imageUrls: {
						media: [
							...(isMarkingAnswer ? [] : imageUrls),
							...(isMarkingAnswer ? [markedImageUrl] : []),
						],
					},
					emotion: {
						audioUrl,
					},
					...(taskType === "audioEmotion" && emotionPredictions
						? { emotionPredictions: JSON.stringify(emotionPredictions) }
						: {}),
					...(assessmentType === "langgraph-speaking" && audioUrl
						? { audioUrls: [audioUrl] }
						: {}),
					...(assessmentType === "langgraph-writing" &&
						existingUserResponse &&
						!isMarkingAnswer && {
							writing_response: String(existingUserResponse).trim(),
						}),
					...(assessmentType === "langgraph-interactive-video" &&
						!isMarkingAnswer &&
						existingUserResponse && {
							patient_doctor_transcript: JSON.stringify(existingUserResponse),
							response: "Check the patient_doctor_transcript",
						}),
					feedbackInputs: {
						language,
						...(isMockedData
							? {
									task_type: taskType,
									imageUrls: {
										media: [
											...(isMarkingAnswer ? [] : imageUrls),
											...(isMarkingAnswer ? [markedImageUrl] : []),
										],
									},
								}
							: {}),
					},
				},
			},
		};

		setStreaming(true);
		setShowError(false);
		// onConfidenceProvided(50);

		if (currentItem.response?.goldStandard) {
			const userDataBody = {
				...body,
				...(audioTranscription && {
					audioTranscription,
				}),
			};
			submitUserData(JSON.stringify(userDataBody));
		}
		const es = new EventSource(ENDPOINT_ASSESSOR, {
			method: "POST",
			headers: { "Content-Type": "text/event-stream" },
			body: JSON.stringify(body),
		});

		es.addEventListener("open", (_event) => {
			// console.info("Open SSE connection.1", event);
		});

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		es.addEventListener("message", (event) => {
			if (!event.data) {
				return;
			}
			const message = JSON.parse(event.data);

			if (message.event === "close") {
				// console.log("closing");
				es.close();
				// return;
			}

			if (message.event === "runDetails") {
				// console.log("runDetails", message.data);
				updateRunDetails(currentItem.id!, message.data);
			}

			if (message.event === "data") {
				const node = message.data.node;
				const response = message.data.response;
				let overallNodeAppeared = false;
				if (node === "subskillAssessmentNode") {
					// console.log("subskillAssessmentNode", response);
					if (response.subskillAssessments) {
						updateSubskillAssessments(
							currentItem.id!,
							response.subskillAssessments,
						);
					}
				}
				if (node === "overallAssessmentNode") {
					if (response.score !== undefined) {
						overallNodeAppeared = true;
						updateScore(currentItem.id!, response.score);
					}
				}
				if (
					overallNodeAppeared &&
					itemType === "AUDIO_INPUT_EMOTION" &&
					emotionPredictions
				) {
					updateTopFiveEmotions(emotionPredictions, currentItem);
				}
				if (node === "feedbackNode") {
					// console.log("feedbackNode", response);
					if (response.feedback) {
						runOnJS(updateExplanation)(currentItem.id!, response.feedback);
					}
				}
			}
		});

		es.addEventListener("close", () => {
			onAiFeedbackProvided();
			setStreaming(false);
		});

		es.addEventListener("error", (event) => {
			console.error("Error from SSE.", event);

			const retries =
				useAIFeedbackStore.getState().aiResponse?.[currentItem.id!].retries ??
				0;

			if (retries < MAX_RETRIES) {
				const incrementRetries = useAIFeedbackStore.getState().incrementRetries;
				incrementRetries(currentItem.id!);
				return;
			}
			showToast({
				message: "Something went wrong, please restart the session",
			});
			es.close();
			// ? Uncomment to use mock data
			// 	updateRunDetails(currentItem.id!, mockRunDetails);
			// updateSubskillAssessments(
			// 	currentItem.id!,
			// 	mockSubskillAssessmentNode.subskillAssessments,
			// );
			// updateScore(currentItem.id!, mockOverallAssessmentNode.score);
			// const words = mockFeedbackNode.feedback.split(" ");
			// for (let i = 0; i < words.length; i += 2) {
			// 	const endIndex = Math.min(i + 2, words.length);
			// 	const partialFeedback = words.slice(0, endIndex).join(" ");
			// 	updateExplanation(currentItem.id!, partialFeedback);
			// 	await new Promise((resolve) => setTimeout(resolve, 25));
			// }
		});
	};

	const generateExplanation = async (onAiFeedbackProvided: () => void) => {
		const currentItem = sessionItems[currentItemIndex];

		const itemType = currentItem.metadata?.type!;
		const renderableItemType = MAPPED_ITEM_TYPES[itemType];
		const existingUserResponse = userResponse?.[currentItem.id!];
		const existingAudioResponse = audioResponse?.[currentItem.id!];

		if (existingUserResponse || existingAudioResponse) {
			setStreaming(true);
			let audioUrl: string | null = null;
			let markedImageUrl: string | null = null;
			let audioTranscription: string | undefined;

			const fileName = `${userId}-${sessionId}-${currentItem.id}-${Date.now()}.${isWeb ? "webm" : "m4a"}`;
			const markedImageFileName = `${userId}-${sessionId}-${currentItem.id}-${Date.now()}.png`;

			let blob = existingUserResponse as Blob;

			if (itemType === "AUDIO_INPUT" || itemType === "AUDIO_INPUT_EMOTION") {
				if (isWeb) {
					blob = await fetch(existingUserResponse as string).then((res) =>
						res.blob(),
					);
				}
				audioUrl = await uploadRecording(blob, fileName);

				const transcriptionResult = await transcribeAudio(blob);

				if (transcriptionResult.success) {
					audioTranscription = transcriptionResult.text;
				}
			}

			if (itemType === "MARKING_ANSWER") {
				markedImageUrl = await uploadImage(blob, markedImageFileName);
			}

			handleGenerateExplanation({
				currentItem,
				itemType,
				renderableItemType,
				onAiFeedbackProvided,
				audioUrl,
				existingUserResponse: existingUserResponse as string,
				markedImageUrl,
				audioTranscription,
			});
		}
	};

	return {
		generateExplanation,
	};
};
